import React, { useState, useEffect } from "react";
import { useGlobal } from "reactn";
import { useHistory } from "react-router-dom";

import DetectDeviceResize from "shared-anpost-components/hooks/detectDeviceResizeHook";
import Loader from "shared-anpost-components/components/loader/Loader";

import Divider from "../../../_core/divider/Divider";
import DeliveryItems from "../../../_core/deliveryItems/DeliveryItems";
import ManageDelivery from "../../../_core/manageDelivery/ManageDelivery";
import HistoricalItems from "../../../_core/historicalItems/HistoricalItems";
import ErrorMessage from "../../../_core/errorMessage";
import PhoneBanner from "../../../_common/phoneBanner";
import ItemStatusNav from "../../../_common/itemStatusNav/ItemStatusNav";
import Icon from "../../../_common/icons/ReturnIcons";
import { MyDeliveriesStyled, StatusWrapper, SettingsWrapper } from "./MyDeliveries.styled";
import { DeliveryManagerMobileStyled } from "./deliveryManagerMobileStyled";
import SingleItemHistory from "../../singleItemHistory";
import SingleNotOwnedItemHistory from "../../singleNotOwnedItemHistory";
import profileHooks from "../../../../hooks/profileHooks";
import { pushDataLayer, sanitizeTrackingNumber } from "../../../../helpers/Helpers";
import { getMobileNumber, getTrackingHistory, getItemSummary, getCustItemSummary } from "../../../../services/Api";
import { ReactComponent as Arrow } from "../../../../images/rightArrow.svg";

const MyDeliveries = () => {
    const [isHistorical, setIsHistorical] = useGlobal("isHistorical");
    const [selectedTrackingItem, setSelectedTrackingItem] = useGlobal("selectedTrackingItem");
    const [notOwnedItemTrackingNumber, setNotOwnedItemTrackingNumber] = useGlobal("notOwnedItemTrackingNumber");
    const [userIsRegisteredToMyDelApp] = useGlobal("userIsRegisteredToMyDelApp");
    const [refetch, setRefetch] = useGlobal("refetch");
    const [selectedTrackingItemDetails, setSelectedTrackingItemDetails] = useGlobal("selectedTrackingItemDetails");
    const [trackingItems, setTrackingItems] = useGlobal("trackingItems");
    const [returnType, setReturnType] = useGlobal("globalReturnType");

    const history = useHistory();

    const { isMobile } = DetectDeviceResize();

    const { values } = profileHooks();

    const [searchValue, setSearchValue] = useState("");
    const [itemFound, setItemFound] = useState(false);
    const [ownedItemFound, setOwnedItemFound] = useState(false);
    const [notOwnedItemFound, setNotOwnedItemFound] = useState(false);
    const [hasMobileNumber, setHasMobileNumber] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [apiError, setApiError] = useState(false);

    useEffect(() => {
        if (!userIsRegisteredToMyDelApp) return;
        const fetchTrackingData = async () => {
            try {
                const res = await getTrackingHistory(values.ciamUserId);
                const items = res.data.trackingItems;

                setTrackingItems(items);

                const queryParams = new URLSearchParams(window.location.search);
                let trackingNumber = queryParams.get("trackingNumber") || queryParams.get("t");
                trackingNumber = sanitizeTrackingNumber(trackingNumber);

                if (!values.loggedIn) {
                    // logged out
                    if (!trackingNumber) {
                        window.location.href = "/Post-Parcels/Receiving/My-deliveries";
                    } else {
                        window.location.href = `/Post-Parcels/Track/History?item=${trackingNumber}`;
                    }
                } else if (trackingNumber) {
                    // logged in
                    const ownedItem = items.find(item => item.trackingNumber.toLowerCase() === trackingNumber.toLowerCase());

                    if (ownedItem) {
                        const trackingItemDetailsRes = await getTrackingHistory(values.ciamUserId, trackingNumber);
                        if (trackingItemDetailsRes && trackingItemDetailsRes.data) {
                            setSelectedTrackingItemDetails(trackingItemDetailsRes.data.trackingItemHistories);
                        }
                        setItemFound(true);
                        setSelectedTrackingItem(ownedItem);
                        setOwnedItemFound(true);
                    } else {
                        const itemSummary = await getItemSummary({
                            getItemSummary: {
                                trackingItems: [trackingNumber]
                            }
                        });

                        if (itemSummary.data.getItemSummaryResponse.GetItemSummaryResult.length > 0) {
                            if (itemSummary.data.getItemSummaryResponse.GetItemSummaryResult.length === 1) {
                                // Single tracking number - not owned item
                                setItemFound(true);
                                setSelectedTrackingItem(null);
                                setNotOwnedItemTrackingNumber(trackingNumber);
                                setNotOwnedItemFound(true);
                                return;
                            }
                        }

                        const custItemSummary = await getCustItemSummary({
                            getCustItemSummary: {
                                customerNumbers: [trackingNumber]
                            }
                        });

                        if (custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult.length > 0) {
                            if (custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult.length === 1) {
                                // Single order number - not owned item
                                setItemFound(true);
                                setSelectedTrackingItem(null);
                                setNotOwnedItemTrackingNumber(custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult[0].anPostNo);
                                setNotOwnedItemFound(true);
                                return;
                            }
                            // Multiple order numbers
                            window.location.href = `/Post-Parcels/Track/Search/Results?rtt=1&items=${trackingNumber}`;
                            return;
                        }

                        // If no results found, redirect to NoResults page
                        window.location.href = `/Post-Parcels/Track/NoResults`;
                    }
                }
            } catch (error) {
                setApiError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrackingData();
    }, [userIsRegisteredToMyDelApp]);

    useEffect(() => {
        setReturnType("receiving");
    }, []);

    useEffect(() => {
        if (!userIsRegisteredToMyDelApp) return;
        const fetchMobileNumber = async () => {
            try {
                const res = await getMobileNumber(values.ciamUserId);
                if (res.data) {
                    if (!res.data.mobileNumbers.length) {
                        setHasMobileNumber(false);
                        pushDataLayer({
                            event: "banner",
                            banner_name: "Phone number - My deliveries"
                        });
                    } else {
                        res.data.mobileNumbers[0].verified ? setHasMobileNumber(true) : setHasMobileNumber(false);
                        if (!res.data.mobileNumbers[0].verified) {
                            pushDataLayer({
                                event: "banner",
                                banner_name: "Phone number - My deliveries"
                            });
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching mobile number", error);
            }
        };

        fetchMobileNumber();
    }, [userIsRegisteredToMyDelApp, refetch]);

    const onSearchValueChange = event => {
        setSearchValue(event.target.value);
        setSearchError("");
    };

    const submitSearch = async event => {
        event.preventDefault();

        const sanitizedTrackingNumber = sanitizeTrackingNumber(searchValue);

        if (sanitizedTrackingNumber.length < 4) {
            setSearchError("Please enter a tracking number");
            return;
        }

        try {
            setIsSearching(true);
            const ownedItem = trackingItems.find(item => item.trackingNumber.toLowerCase() === sanitizedTrackingNumber.toLowerCase());

            if (ownedItem) {
                const trackingItemDetailsRes = await getTrackingHistory(values.ciamUserId, sanitizedTrackingNumber);
                if (trackingItemDetailsRes && trackingItemDetailsRes.data) {
                    setSelectedTrackingItemDetails(trackingItemDetailsRes.data.trackingItemHistories);
                }

                pushDataLayer({
                    event: "myDeTrackSearch",
                    search_term: sanitizedTrackingNumber,
                    result: "found"
                });
                setItemFound(true);
                setSelectedTrackingItem(ownedItem);
                setOwnedItemFound(true);
            } else {
                const itemSummary = await getItemSummary({
                    getItemSummary: {
                        trackingItems: [sanitizedTrackingNumber]
                    }
                });

                if (itemSummary.data.getItemSummaryResponse.GetItemSummaryResult.length > 0) {
                    if (itemSummary.data.getItemSummaryResponse.GetItemSummaryResult.length === 1) {
                        // Single tracking number - not owned item
                        pushDataLayer({
                            event: "myDeTrackSearch",
                            search_term: sanitizedTrackingNumber,
                            result: "not found"
                        });
                        pushDataLayer({
                            event: "banner",
                            banner_name: "Phone number - My deliveries"
                        });
                        setItemFound(true);
                        setSelectedTrackingItem(null);
                        setNotOwnedItemTrackingNumber(sanitizedTrackingNumber);
                        setNotOwnedItemFound(true);
                        return;
                    }
                }

                const custItemSummary = await getCustItemSummary({
                    getCustItemSummary: {
                        customerNumbers: [sanitizedTrackingNumber]
                    }
                });

                if (custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult.length > 0) {
                    if (custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult.length === 1) {
                        // Single order number - not owned item
                        pushDataLayer({
                            event: "myDeTrackSearch",
                            search_term: custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult[0].anPostNo,
                            result: "not found"
                        });
                        pushDataLayer({
                            event: "banner",
                            banner_name: "Phone number - My deliveries"
                        });
                        setItemFound(true);
                        setSelectedTrackingItem(null);
                        setNotOwnedItemTrackingNumber(custItemSummary.data.getCustItemSummaryResponse.GetCustItemSummaryResult[0].anPostNo);
                        setNotOwnedItemFound(true);
                        return;
                    }
                    // Multiple order numbers
                    setSearchValue("");
                    window.location.href = `/Post-Parcels/Track/Search/Results?rtt=1&items=${sanitizedTrackingNumber}`;
                    return;
                }

                // If no results found, redirect to NoResults page
                pushDataLayer({
                    event: "myDeTrackSearch",
                    search_term: sanitizedTrackingNumber,
                    result: "not found"
                });
                setSearchValue("");
                window.location.href = `/Post-Parcels/Track/NoResults`;
            }
        } catch (error) {
            console.error("Error fetching tracking history", error);
        } finally {
            setIsSearching(false);
        }
    };

    const goBackToAllItems = () => {
        setItemFound(false);
        history.push("/"); // * Forces a re-render of the component and all useEffect
        setSelectedTrackingItem(null);
        if (ownedItemFound) {
            setNotOwnedItemFound(false);
        } else if (notOwnedItemFound) {
            setOwnedItemFound(false);
        }
    };

    // Recheck unowned item if can be owned by user, after verifying phone number
    const recheckUnownedItem = async () => {
        try {
            setIsLoading(true);
            const res = await getTrackingHistory(values.ciamUserId);
            const items = res.data.trackingItems;

            setTrackingItems(items);

            // If item page was accessed directly with tracking number in URL or by email link,
            // get the tracking number from URL and check if it's in the list of items.
            // Otherwise, use the tracking number from the search input.
            const queryParams = new URLSearchParams(window.location.search);
            let trackingNumber = queryParams.get("trackingNumber") || queryParams.get("t") || searchValue;
            trackingNumber = sanitizeTrackingNumber(trackingNumber);

            const ownedItem = items.find(item => item.trackingNumber.toLowerCase() === trackingNumber.toLowerCase());

            if (ownedItem) {
                const trackingItemDetailsRes = await getTrackingHistory(values.ciamUserId, trackingNumber);
                if (trackingItemDetailsRes && trackingItemDetailsRes.data) {
                    setSelectedTrackingItemDetails(trackingItemDetailsRes.data.trackingItemHistories);
                }
                setItemFound(true);
                setSelectedTrackingItem(ownedItem);
                setNotOwnedItemTrackingNumber("");
                setOwnedItemFound(true);
                setNotOwnedItemFound(false);
            } else {
                setItemFound(true);
                setSelectedTrackingItem(null);
                setNotOwnedItemTrackingNumber(trackingNumber);
                setOwnedItemFound(false);
                setNotOwnedItemFound(true);
            }
        } catch (error) {
            console.error("Error fetching mobile number", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <Loader />;

    return (
        <>
            {isSearching && <Loader />}
            {itemFound ? (
                // Single item
                <SettingsWrapper>
                    {!hasMobileNumber && notOwnedItemFound && <PhoneBanner heading="Can’t manage this delivery?" singleItem onVerified={recheckUnownedItem} />}

                    <button type="button" className="backButton" onClick={goBackToAllItems}>
                        <Arrow className="arrow" />
                        <span>Back</span>
                    </button>

                    <div>
                        <p className="pageTitle">Item Detail</p>
                    </div>

                    {ownedItemFound && <SingleItemHistory />}
                    {notOwnedItemFound && <SingleNotOwnedItemHistory />}
                </SettingsWrapper>
            ) : (
                <>
                    {/* All items */}

                    {isMobile && selectedTrackingItem ? (
                        <DeliveryManagerMobileStyled deliveryManagerExist={!!selectedTrackingItem}>
                            <ManageDelivery />
                        </DeliveryManagerMobileStyled>
                    ) : (
                        <>
                            {!hasMobileNumber && <PhoneBanner />}

                            {!isHistorical && (
                                <StatusWrapper>
                                    <ItemStatusNav />

                                    <form className="inputWrapper" onSubmit={submitSearch}>
                                        <input type="text" placeholder="Enter tracking or order number" value={searchValue} onChange={onSearchValueChange} />
                                        <p className="searchErrorInfo">{searchError}</p>
                                        <button className="active" type="submit">
                                            <Icon src="searchIcon" />
                                        </button>
                                    </form>
                                </StatusWrapper>
                            )}

                            <Divider>
                                {isHistorical ? (
                                    <HistoricalItems history={history} handleHistoricalStatus={() => setIsHistorical(false)} />
                                ) : (
                                    <>
                                        <MyDeliveriesStyled>
                                            {!isMobile && (
                                                <>
                                                    {" "}
                                                    {apiError ? (
                                                        <ErrorMessage
                                                            tryAgainEvent={() => window.location.reload()}
                                                            message={"We're sorry but we can't retrieve your delivery information right now."}
                                                        />
                                                    ) : (
                                                        <DeliveryItems />
                                                    )}{" "}
                                                </>
                                            )}
                                            {isMobile && (
                                                <>
                                                    {apiError ? (
                                                        <ErrorMessage
                                                            tryAgainEvent={() => window.location.reload()}
                                                            message={"We're sorry but we can't retrieve your delivery information right now."}
                                                        />
                                                    ) : (
                                                        <DeliveryItems />
                                                    )}
                                                </>
                                            )}
                                        </MyDeliveriesStyled>
                                        {!isMobile && <ManageDelivery />}
                                    </>
                                )}
                            </Divider>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default MyDeliveries;
